import { useState } from 'react';
import {
  Box, TextField, Snackbar, IconButton, Typography, Link,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import { LoadingButton } from './components';
import getLoginTokens from './loginTokens';

const useStyles = makeStyles()((theme) => {
  const P = getLoginTokens(theme.palette.mode === 'dark', theme.palette.primary.main);
  return {
    formCard: {
      animation: 'loginSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      opacity: 0,
    },
    customInput: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px !important',
        backgroundColor: `${P.inputBg} !important`,
        fontSize: '0.92rem',
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: `${P.inputBorder} !important`,
        borderWidth: '1.5px !important',
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: `${P.inputBorderHover} !important`,
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: `${P.primary} !important`,
        borderWidth: '1.5px !important',
        boxShadow: `0 0 0 3px ${P.focusShadow}`,
      },
      '& .MuiOutlinedInput-input': {
        padding: '0.85rem 1rem',
      },
    },
  };
});

const ResetPasswordPage = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const P = getLoginTokens(isDark, theme.palette.primary.main);
  const t = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await fetch('/api/users/send-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setSuccess(true);
        setSnackbar(t('loginResetEmailSent'));
        setEmail('');
        setTimeout(() => setSuccess(false), 2000);
      } else {
        setSnackbar(await response.text());
      }
    } catch (error) {
      setSnackbar(error.message);
    }
    setLoading(false);
  };

  const labelSx = {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: P.textPrimary,
    mb: 0.5,
  };

  return (
    <LoginLayout>
      <style>{`@keyframes loginSlideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }`}</style>

      <Box className={classes.formCard} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: P.textPrimary,
            mb: 1,
            textAlign: 'center',
            letterSpacing: '-0.01em',
            fontSize: '1.55rem',
          }}
        >
          {t('loginReset')}
        </Typography>
        <Typography
          sx={{
            color: P.textSecondary,
            fontSize: '0.88rem',
            textAlign: 'center',
            mb: 4,
          }}
        >
          {t('loginResetDescription')}
        </Typography>

        <Box component="form" onSubmit={handleReset} noValidate>
          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="reset-email" sx={labelSx}>
              {t('userEmail')}
            </Typography>
            <TextField
              id="reset-email"
              required
              fullWidth
              placeholder={t('loginEmailPlaceholder')}
              name="email"
              value={email}
              autoComplete="email"
              autoFocus
              variant="outlined"
              size="small"
              className={classes.customInput}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Box>

          <LoadingButton
            onClick={handleReset}
            loading={loading}
            success={success}
            disabled={!email}
          >
            {t('loginReset')}
          </LoadingButton>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 2.5,
              fontSize: '0.85rem',
              color: P.textSecondary,
            }}
          >
            {`${t('loginRememberPassword')} `}
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/login')}
              underline="none"
              sx={{
                color: P.primary,
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                '&:hover': { color: P.primaryHover },
              }}
            >
              {t('loginLogin')}
            </Link>
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setSnackbar('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default ResetPasswordPage;
